import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import useCurrentUser from "hooks/useCurrentUser";
import {
  PageContainer, DataTable, PremiumModal, PremiumCard, Button,
  EmptyState,
} from "components/ui";

const Purchases = () => {
  const axiosPrivate = useAxiosPrivate();
  const { user } = useCurrentUser();
  const branch = user?.branch;

  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const size = 10;

  const fetchPurchases = async () => {
    if (!branch) return;
    setLoading(true);
    try {
      const res = await axiosPrivate.get(`/api/purchases?page=${page}&size=${size}`);
      setPurchases(res.data.content);
      setTotalPages(res.data.page.totalPages);
      setTotalElements(res.data.page.totalElements);
    } catch (_) {
      toast.error("Failed to load purchases");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPurchases(); }, [branch, page]);

  if (!branch) {
    return (
      <PageContainer>
        <PremiumCard><EmptyState title="No branch assigned" /></PremiumCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer className="mt-4">
      <DataTable
        title="Purchase history"
        subtitle={`${totalElements} record${totalElements !== 1 ? "s" : ""}`}
        loading={loading}
        data={purchases}
        rowKey={(p) => p.id}
        search={false}
        paginate={false}
        emptyTitle="No purchases recorded"
        columns={[
          { key: "purchaseDate",  header: "Date",     render: (p) => <span className="t-muted t-mono" style={{ fontSize: 12 }}>{new Date(p.purchaseDate).toLocaleDateString()}</span> },
          { key: "supplierName",  header: "Supplier", render: (p) => p.supplierName || "—" },
          { key: "invoiceNumber", header: "Invoice",  render: (p) => <span className="t-mono">{p.invoiceNumber || "—"}</span> },
          { key: "itemCount",     header: "Items",    align: "right", render: (p) => p.items?.length || 0 },
          { key: "totalAmount",   header: "Total",    align: "right", render: (p) => <strong className="t-mono">{Number(p.totalAmount).toFixed(3)} TND</strong> },
          {
            key: "actions", header: "", align: "right",
            render: (p) => (
              <Button variant="secondary" size="sm" onClick={() => { setSelectedPurchase(p); setModalOpen(true); }}>
                View items
              </Button>
            ),
          },
        ]}
        footer={
          totalPages > 1 ? (
            <div className="dt-foot">
              <span>Page <strong>{page + 1}</strong> of {totalPages}</span>
              <div style={{ display: "inline-flex", gap: 6 }}>
                <Button variant="ghost" size="sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>‹ Prev</Button>
                <Button variant="ghost" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}>Next ›</Button>
              </div>
            </div>
          ) : null
        }
      />

      {selectedPurchase && (
        <PremiumModal
          isOpen={modalOpen}
          toggle={() => setModalOpen(false)}
          title={`Invoice ${selectedPurchase.invoiceNumber || ""} — ${selectedPurchase.supplierName || ""}`}
          size="lg"
          footer={<Button variant="secondary" onClick={() => setModalOpen(false)}>Close</Button>}
        >
          <p className="t-body-sm t-muted" style={{ marginBottom: 16 }}>
            Date: {new Date(selectedPurchase.purchaseDate).toLocaleString()}
          </p>
          <div className="dt-shell" style={{ boxShadow: "none" }}>
            <div className="dt-scroll">
              <table className="dt-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th style={{ textAlign: "right" }}>Quantity</th>
                    <th style={{ textAlign: "right" }}>Unit price</th>
                    <th style={{ textAlign: "right" }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedPurchase.items || []).map((item, i) => (
                    <tr key={i}>
                      <td>{item.productName || "—"}</td>
                      <td className="num">{item.quantity}</td>
                      <td className="num">{Number(item.unitPrice).toFixed(3)}</td>
                      <td className="num"><strong>{Number(item.totalPrice).toFixed(3)}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Amount breakdown (goods / VAT / stamp duty / total) ── */}
          <div
            style={{
              border: "1px solid var(--border-subtle)",
              borderRadius: 14,
              padding: 20,
              background: "var(--surface-muted)",
              marginTop: 20,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
              <span>Goods amount</span>
              <strong className="t-mono">{Number(selectedPurchase.goodsAmount).toFixed(3)} TND</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
              <span>VAT (déductible)</span>
              <strong className="t-mono">{Number(selectedPurchase.vatAmount).toFixed(3)} TND</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--border-subtle)" }}>
              <span>Stamp duty</span>
              <strong className="t-mono">{Number(selectedPurchase.stampDutyAmount).toFixed(3)} TND</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0 0", fontWeight: 700 }}>
              <span>Total (incl. tax & stamp)</span>
              <span className="t-mono">{Number(selectedPurchase.totalAmount).toFixed(3)} TND</span>
            </div>
          </div>
        </PremiumModal>
      )}
    </PageContainer>
  );
};

export default Purchases;